import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import logger from '../lib/logger';
import { 
  ArrowLeft, Settings as SettingsIcon, DollarSign, Percent, 
  Save, RotateCcw, Lock, AlertCircle, Check, Upload, Download, Layers, Search, Building2,
  Users, Plus, Trash2, Eye, Briefcase, Clock, Calendar, MessageSquareText
} from 'lucide-react';
import { toast } from 'sonner';
import { COMPONENT_INFO } from './estimator/constants';
import PublicCloudReleasesEditor from '../components/PublicCloudReleasesEditor';
import NamedResourcesTab from '../components/admin/NamedResourcesTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [rateCard, setRateCard] = useState({});
  const [implTypes, setImplTypes] = useState({});
  const [modifierRanges, setModifierRanges] = useState({});
  const [newDevSizes, setNewDevSizes] = useState({});

  // Component configuration state
  const [compData, setCompData] = useState([]);
  const [compLoading, setCompLoading] = useState(false);
  const [compFilter, setCompFilter] = useState('');

  // Volume metrics state
  const [volumeMetrics, setVolumeMetrics] = useState([]);
  const [volumeLoading, setVolumeLoading] = useState(false);
  const [volumeFilter, setVolumeFilter] = useState('');
  const [volumeEditing, setVolumeEditing] = useState({});

  useEffect(() => {
    fetchSettings();
    if (isSuperAdmin) { fetchComponents(); fetchVolumeMetrics(); }
  }, [isSuperAdmin]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`, { withCredentials: true });
      setSettings(response.data);
      setRateCard(response.data.rate_card || {});
      setImplTypes(response.data.implementation_types || {});
      setModifierRanges(response.data.modifier_ranges || {});
      setNewDevSizes(response.data.new_dev_sizes || {});
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchComponents = async () => {
    setCompLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/settings/components`, { withCredentials: true });
      setCompData(response.data.components || []);
    } catch (err) {
      // Not a hard failure — the components admin surface is super-admin
      // only, so a 403 here is expected for regular admins. Log the
      // underlying reason for real errors (network, 5xx) to aid support.
      if (err?.response?.status !== 403) {
        logger.warn('settings:fetchComponents failed', err?.response?.status || err?.message);
      }
    } finally {
      setCompLoading(false);
    }
  };

  const fetchVolumeMetrics = async () => {
    setVolumeLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/settings/volume-metrics`, { withCredentials: true });
      setVolumeMetrics(response.data.metrics || []);
    } catch (err) { logger.error('Failed to load volume metrics:', err?.message); }
    finally { setVolumeLoading(false); }
  };

  const saveVolumeMetrics = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await axios.put(`${API_URL}/api/settings/volume-metrics`, { metrics: volumeMetrics }, { withCredentials: true });
      setSuccess('Volume metrics saved'); setVolumeEditing({});
    } catch (err) { setError('Failed to save volume metrics'); }
    finally { setSaving(false); }
  };

  const resetVolumeMetrics = async () => {
    if (!window.confirm('Reset all volume metrics to defaults?')) return;
    try {
      await axios.post(`${API_URL}/api/settings/volume-metrics/reset`, {}, { withCredentials: true });
      setSuccess('Volume metrics reset to defaults');
      fetchVolumeMetrics();
    } catch (err) { setError('Failed to reset'); }
  };

  const exportVolumeCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/volume-metrics/export`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', 'volume_metrics.csv');
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { setError('Export failed'); }
  };

  const importVolumeCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData(); formData.append('file', file);
    try {
      const resp = await axios.post(`${API_URL}/api/settings/volume-metrics/import`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(resp.data.message); fetchVolumeMetrics();
    } catch (err) { setError('Import failed'); }
    e.target.value = '';
  };

  const updateVolumeMetric = (idx, field, value) => {
    const updated = [...volumeMetrics];
    updated[idx] = { ...updated[idx], [field]: parseFloat(value) || 0 };
    setVolumeMetrics(updated);
    setVolumeEditing(prev => ({ ...prev, [idx]: true }));
  };

  const handleSave = async () => {
    if (!isAdmin) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/api/settings`, {
        rate_card: rateCard,
        implementation_types: implTypes,
        modifier_ranges: modifierRanges,
        new_dev_sizes: newDevSizes
      }, { withCredentials: true });
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!isSuperAdmin) return;
    if (!window.confirm('Reset all settings to default values?')) return;
    setSaving(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/api/settings/reset`, {}, { withCredentials: true });
      setSettings(response.data.settings);
      setRateCard(response.data.settings.rate_card);
      setImplTypes(response.data.settings.implementation_types);
      setModifierRanges(response.data.settings.modifier_ranges);
      setNewDevSizes(response.data.settings.new_dev_sizes || {});
      setSuccess('Settings reset to defaults!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const updateRateCard = (role, value) => {
    setRateCard(prev => ({ ...prev, [role]: parseFloat(value) || 0 }));
  };

  const updateImplType = (type, field, value) => {
    setImplTypes(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: field === 'multiplier' ? parseFloat(value) || 1.0 : value }
    }));
  };

  const updateModifierRange = (modifier, level, value) => {
    setModifierRanges(prev => ({
      ...prev,
      [modifier]: { ...prev[modifier], [level]: parseFloat(value) || 1.0 }
    }));
  };

  // Component hour editing
  const updateCompHours = (index, level, value) => {
    setCompData(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        base_hours: { ...updated[index].base_hours, [level]: parseFloat(value) || 0 }
      };
      return updated;
    });
  };

  const updateCompRationale = (index, field, value) => {
    setCompData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const saveComponentHours = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {};
      compData.forEach(comp => {
        payload[comp.name] = {
          base_hours: comp.base_hours,
          required_reason: comp.required_reason || '',
          recommended_reason: comp.recommended_reason || '',
        };
      });
      await axios.put(`${API_URL}/api/settings/components`, { components: payload }, { withCredentials: true });
      setSuccess('Component hours saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save component hours');
    } finally {
      setSaving(false);
    }
  };

  const resetComponentHours = async () => {
    if (!window.confirm('Reset all component hours to system defaults?')) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/settings/components/reset`, {}, { withCredentials: true });
      await fetchComponents();
      setSuccess('Component hours reset to defaults!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to reset component hours');
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/settings/components/export`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'component_hours.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export component hours. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const importCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setSaving(true);
    setError('');
    try {
      const resp = await axios.post(`${API_URL}/api/settings/components/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(`Imported hours for ${resp.data.imported} components`);
      await fetchComponents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
    } finally {
      setSaving(false);
      e.target.value = '';
    }
  };

  const isCustom = (comp) => {
    if (!comp.default_hours || !comp.base_hours) return false;
    return comp.base_hours.simple !== comp.default_hours.simple ||
           comp.base_hours.standard !== comp.default_hours.standard ||
           comp.base_hours.complex !== comp.default_hours.complex;
  };

  const filteredComps = compData.filter(c => 
    !compFilter || c.group.toLowerCase().includes(compFilter.toLowerCase()) || c.name.toLowerCase().includes(compFilter.toLowerCase()) || (c.display_name || '').toLowerCase().includes(compFilter.toLowerCase())
  );

  const ROLE_LABELS = {
    PM: 'Project Manager (PM)',
    CS: 'Consultant Senior (CS)',
    CF: 'Configuration / Functional (CF)',
    IN: 'Integration (IN)',
    DM: 'Data Migration (DM)',
    DE: 'Development (DE)',
    IF: 'Infrastructure (IF)',
    HC: 'Hypercare (HC)'
  };

  const IMPL_TYPE_LABELS = {
    Corporate: 'Corporate',
    EXE_Ecommerce: 'Extended Enterprise / Ecommerce',
    Workforce_Enablement: 'Workforce Enablement'
  };

  const MODIFIER_LABELS = {
    governance: 'Governance Maturity',
    content_readiness: 'Content Readiness',
    integration_complexity: 'Integration Complexity',
    org_complexity: 'Organizational Complexity',
    timeline_pressure: 'Timeline Pressure',
    client_capability: 'Client Team Capability',
    change_readiness: 'Change Readiness',
    data_integrity: 'Data Integrity'
  };

  const LEVEL_LABELS = {
    very_strong: 'Very Strong',
    strong: 'Strong',
    above_average: 'Above Avg',
    neutral: 'Neutral',
    below_average: 'Below Avg',
    weak: 'Weak',
    very_weak: 'Very Weak'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#4ABFCF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {isSuperAdmin ? 'Configure pricing and calculation parameters' : 'View pricing and calculation parameters'}
            </p>
            {isSuperAdmin && (
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleReset}
                  disabled={saving}
                  className="text-slate-600 hover:text-red-600 hover:border-red-300"
                  data-testid="reset-settings-btn"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  Reset
                </Button>
                <Button 
                  onClick={handleSave}
                  size="sm"
                  disabled={saving}
                  className="bg-[#0F2040] hover:bg-[#172E5A] text-white"
                  data-testid="save-settings-btn"
                >
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-700">
            <Check className="w-5 h-5" />
            {success}
          </div>
        )}
        
        {!isSuperAdmin && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3 text-amber-700">
            <Lock className="w-5 h-5" />
            You are viewing settings in read-only mode. Only Super Admins can make changes.
          </div>
        )}

        <Tabs defaultValue="rate-card" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 flex flex-wrap h-auto justify-start gap-1 w-full">
            <TabsTrigger value="rate-card" className="data-[state=active]:bg-[#0F2040] data-[state=active]:text-white">
              <DollarSign className="w-4 h-4 mr-2" />
              Rate Card
            </TabsTrigger>
            <TabsTrigger value="impl-types" className="data-[state=active]:bg-[#0F2040] data-[state=active]:text-white">
              <Percent className="w-4 h-4 mr-2" />
              Implementation Types
            </TabsTrigger>
            <TabsTrigger value="modifiers" className="data-[state=active]:bg-[#0F2040] data-[state=active]:text-white">
              <SettingsIcon className="w-4 h-4 mr-2" />
              Modifier Ranges
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="components" className="data-[state=active]:bg-[#0F2040] data-[state=active]:text-white" data-testid="components-tab">
                <Layers className="w-4 h-4 mr-2" />
                Component Hours
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="volume-metrics" className="data-[state=active]:bg-[#0F2040] data-[state=active]:text-white" data-testid="volume-metrics-tab">
                <Layers className="w-4 h-4 mr-2" />
                Volume Metrics
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="client-resources" className="data-[state=active]:bg-[#0F2040] data-[state=active]:text-white" data-testid="client-resources-tab">
                <Users className="w-4 h-4 mr-2" />
                Client Resources
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="named-resources" className="data-[state=active]:bg-[#0F2040] data-[state=active]:text-white" data-testid="named-resources-tab">
                <Users className="w-4 h-4 mr-2" />
                Named Resources
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="new-dev-config" className="data-[state=active]:bg-[#0F2040] data-[state=active]:text-white" data-testid="new-dev-config-tab">
                <Layers className="w-4 h-4 mr-2" />
                New Development
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="public-cloud-releases" className="data-[state=active]:bg-[#0F2040] data-[state=active]:text-white" data-testid="public-cloud-releases-tab">
                <Calendar className="w-4 h-4 mr-2" />
                Cloud Releases
              </TabsTrigger>
            )}
            <TabsTrigger value="estimates-history" className="data-[state=active]:bg-[#0F2040] data-[state=active]:text-white" data-testid="estimates-history-tab">
              <Clock className="w-4 h-4 mr-2" />
              Estimates History
            </TabsTrigger>
          </TabsList>

          {/* Rate Card Tab */}
          <TabsContent value="rate-card">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-['Outfit']">Hourly Rate Card</CardTitle>
                <CardDescription>Set the hourly rate for each role type used in cost calculations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {Object.entries(rateCard).map(([role, rate]) => (
                    <div key={role} className="space-y-2">
                      <Label htmlFor={`rate-${role}`} className="text-sm font-medium">
                        {ROLE_LABELS[role] || role}
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                        <Input
                          id={`rate-${role}`}
                          type="number"
                          value={rate}
                          onChange={(e) => updateRateCard(role, e.target.value)}
                          disabled={!isSuperAdmin}
                          className="pl-8"
                          data-testid={`rate-input-${role}`}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">/hour</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Implementation Types Tab */}
          <TabsContent value="impl-types">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-['Outfit']">Implementation Type Multipliers</CardTitle>
                <CardDescription>Configure effort multipliers for each implementation type</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(implTypes).map(([type, config]) => (
                  <div key={type} className="p-4 border border-slate-200 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-[#0F2040]">{IMPL_TYPE_LABELS[type] || type}</h3>
                      <Badge variant="outline">{config.multiplier}x multiplier</Badge>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor={`impl-mult-${type}`}>Effort Multiplier</Label>
                        <Input
                          id={`impl-mult-${type}`}
                          type="number"
                          step="0.1"
                          min="0.5"
                          max="3.0"
                          value={config.multiplier}
                          onChange={(e) => updateImplType(type, 'multiplier', e.target.value)}
                          disabled={!isSuperAdmin}
                          data-testid={`impl-multiplier-${type}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`impl-desc-${type}`}>Description</Label>
                        <Input
                          id={`impl-desc-${type}`}
                          type="text"
                          value={config.description}
                          onChange={(e) => updateImplType(type, 'description', e.target.value)}
                          disabled={!isSuperAdmin}
                          data-testid={`impl-desc-${type}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Modifier Ranges Tab */}
          <TabsContent value="modifiers">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-['Outfit']">Modifier Ranges</CardTitle>
                <CardDescription>Configure the effort multiplier for each modifier level</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(modifierRanges).map(([modifier, levels]) => {
                  const levelCount = Object.keys(levels).length;
                  const gridCols = levelCount <= 5 ? 'grid-cols-5' : 'grid-cols-7';
                  return (
                    <div key={modifier} className={`p-4 border rounded-lg space-y-4 ${modifier === 'data_integrity' ? 'border-rose-200 bg-rose-50/20' : 'border-slate-200'}`}>
                      <h3 className="font-semibold text-[#0F2040]">{MODIFIER_LABELS[modifier] || modifier}</h3>
                      <div className={`grid ${gridCols} gap-3`}>
                        {Object.entries(levels).map(([level, value]) => (
                          <div key={level} className="space-y-1">
                            <Label className="text-xs text-slate-500">{LEVEL_LABELS[level] || level}</Label>
                            <Input
                              type="number"
                              step="0.05"
                              min="0.5"
                              max="2.5"
                              value={value}
                              onChange={(e) => updateModifierRange(modifier, level, e.target.value)}
                              disabled={!isSuperAdmin}
                              className="text-center text-sm"
                              data-testid={`modifier-${modifier}-${level}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Component Hours Tab - Super Admin Only */}
          {isSuperAdmin && (
            <TabsContent value="components">
              <Card>
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <CardTitle className="text-xl font-['Outfit']">Component Base Hours</CardTitle>
                      <CardDescription>Edit base hours for each complexity level. Changes apply to all new estimates.</CardDescription>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
                        <Download className="w-4 h-4 mr-1.5" />
                        Export CSV
                      </Button>
                      <label>
                        <Button variant="outline" size="sm" asChild data-testid="import-csv-btn">
                          <span>
                            <Upload className="w-4 h-4 mr-1.5" />
                            Import CSV
                          </span>
                        </Button>
                        <input type="file" accept=".csv" onChange={importCSV} className="hidden" />
                      </label>
                      <Button variant="outline" size="sm" onClick={resetComponentHours} className="text-slate-600 hover:text-red-600 hover:border-red-300" data-testid="reset-comp-hours-btn">
                        <RotateCcw className="w-4 h-4 mr-1.5" />
                        Reset All
                      </Button>
                      <Button size="sm" onClick={saveComponentHours} disabled={saving} className="bg-[#0F2040] hover:bg-[#172E5A] text-white" data-testid="save-comp-hours-btn">
                        <Save className="w-4 h-4 mr-1.5" />
                        Save Hours
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Search */}
                  <div className="relative mb-6 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search components..."
                      value={compFilter}
                      onChange={e => setCompFilter(e.target.value)}
                      className="pl-10"
                      data-testid="comp-filter-input"
                    />
                  </div>

                  {compLoading ? (
                    <div className="text-center py-12 text-slate-400">Loading components...</div>
                  ) : (
                    <div className="space-y-8">
                      {(() => {
                        const groups = {};
                        filteredComps.forEach(comp => {
                          const g = comp.group || 'Other';
                          if (!groups[g]) groups[g] = [];
                          groups[g].push(comp);
                        });
                        
                        return Object.entries(groups).map(([group, comps]) => (
                          <div key={group} className="border border-slate-200 rounded-xl overflow-hidden">
                            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                              <h3 className="font-semibold text-[#0F2040] text-sm">{group}</h3>
                              <span className="text-xs text-slate-400">{comps.length} component{comps.length !== 1 ? 's' : ''}</span>
                            </div>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-slate-100 bg-white">
                                  <th className="text-left py-2 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Component</th>
                                  <th className="text-center py-2 px-2 text-xs font-medium text-slate-500 uppercase tracking-wider w-20">Role</th>
                                  <th className="text-center py-2 px-2 text-xs font-medium text-slate-500 uppercase tracking-wider w-24">Simple</th>
                                  <th className="text-center py-2 px-2 text-xs font-medium text-slate-500 uppercase tracking-wider w-24">Standard</th>
                                  <th className="text-center py-2 px-2 text-xs font-medium text-slate-500 uppercase tracking-wider w-24">Complex</th>
                                  <th className="text-center py-2 px-2 text-xs font-medium text-slate-500 uppercase tracking-wider w-24">Rationale</th>
                                  <th className="w-16"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {comps.map((comp) => {
                                  const origIdx = compData.findIndex(c => c.name === comp.name);
                                  const custom = isCustom(comp);
                                  return (
                                    <tr key={comp.name} className={`border-b border-slate-50 hover:bg-slate-50/50 ${custom ? 'bg-blue-50/30' : ''}`}>
                                      <td className="py-2.5 px-4">
                                        <span className="font-medium text-[#0F2040]">{comp.display_name || comp.name}</span>
                                        {comp.sub_group && <span className="text-xs text-slate-400 ml-2">{comp.sub_group}</span>}
                                      </td>
                                      <td className="py-2.5 px-2 text-center">
                                        <Badge variant="secondary" className="text-[10px]">{comp.role}</Badge>
                                      </td>
                                      <td className="py-2.5 px-2">
                                        <Input
                                          type="number"
                                          min="0"
                                          value={comp.base_hours?.simple ?? 0}
                                          onChange={e => updateCompHours(origIdx, 'simple', e.target.value)}
                                          className="text-center h-8 text-sm"
                                        />
                                      </td>
                                      <td className="py-2.5 px-2">
                                        <Input
                                          type="number"
                                          min="0"
                                          value={comp.base_hours?.standard ?? 0}
                                          onChange={e => updateCompHours(origIdx, 'standard', e.target.value)}
                                          className="text-center h-8 text-sm"
                                        />
                                      </td>
                                      <td className="py-2.5 px-2">
                                        <Input
                                          type="number"
                                          min="0"
                                          value={comp.base_hours?.complex ?? 0}
                                          onChange={e => updateCompHours(origIdx, 'complex', e.target.value)}
                                          className="text-center h-8 text-sm"
                                        />
                                      </td>
                                      <td className="py-2.5 px-2 text-center">
                                        {(comp.required || comp.recommended) ? (
                                          <Popover>
                                            <PopoverTrigger asChild>
                                              <Button variant="ghost" size="sm"
                                                className={`h-7 px-2 ${(comp.required_reason || comp.recommended_reason) ? 'text-[#4ABFCF]' : 'text-slate-400'}`}
                                                data-testid={`rationale-btn-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                                <MessageSquareText className="w-3.5 h-3.5" />
                                              </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-80" side="left">
                                              {(() => {
                                                const field = comp.required ? 'required_reason' : 'recommended_reason';
                                                const currentValue = comp[field] || '';
                                                const staticDefault = COMPONENT_INFO[comp.name]?.hours_rationale || '';
                                                return (
                                                  <div className="space-y-2">
                                                    <div>
                                                      <Label className="text-xs font-semibold text-[#0F2040]">
                                                        {comp.required ? 'Why is this Required?' : 'Why is this Recommended?'}
                                                      </Label>
                                                      <p className="text-[10px] text-slate-400 mt-0.5">
                                                        Shown on hover of the {comp.required ? 'Required' : 'Recommended'} badge. Leave blank to fall back to default.
                                                      </p>
                                                    </div>
                                                    <Textarea
                                                      value={currentValue}
                                                      onChange={e => updateCompRationale(origIdx, field, e.target.value)}
                                                      placeholder={staticDefault || (comp.required
                                                        ? 'e.g. Project governance is non-negotiable for Seertech audits.'
                                                        : 'e.g. Strongly recommended to reduce first-month incident volume.')}
                                                      rows={4} className="text-xs"
                                                      data-testid={`rationale-textarea-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}
                                                    />
                                                    <div className="flex items-center justify-between gap-2 pt-1">
                                                      <p className="text-[10px] text-slate-400 flex-1">
                                                        Saves with the main button below.
                                                      </p>
                                                      {staticDefault ? (
                                                        <Button type="button" variant="ghost" size="sm"
                                                          onClick={() => updateCompRationale(origIdx, field, staticDefault)}
                                                          className="h-6 px-2 text-[10px] text-[#4ABFCF] hover:bg-[#4ABFCF]/5 hover:text-[#4ABFCF] shrink-0"
                                                          data-testid={`rationale-default-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                                          <RotateCcw className="w-3 h-3 mr-1" /> Use default
                                                        </Button>
                                                      ) : (
                                                        <Button type="button" variant="ghost" size="sm"
                                                          onClick={() => updateCompRationale(origIdx, field, '')}
                                                          className="h-6 px-2 text-[10px] text-slate-400 hover:text-slate-500 shrink-0"
                                                          data-testid={`rationale-clear-${comp.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                                          Clear
                                                        </Button>
                                                      )}
                                                    </div>
                                                  </div>
                                                );
                                              })()}
                                            </PopoverContent>
                                          </Popover>
                                        ) : (
                                          <span className="text-[10px] text-slate-300">—</span>
                                        )}
                                      </td>
                                      <td className="py-2.5 px-2 text-center">
                                        {custom && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Custom</Badge>}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Volume Metrics Tab */}
          {isSuperAdmin && (
            <TabsContent value="volume-metrics">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg" data-testid="volume-metrics-title">Volume Metrics</CardTitle>
                      <CardDescription>Manage per-volume effort rates for component sub-items. These allow users to override templated effort with volume-based calculations.</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={exportVolumeCSV} data-testid="volume-export-btn">
                        <Download className="w-4 h-4 mr-1" /> Export CSV
                      </Button>
                      <label>
                        <Button variant="outline" size="sm" asChild data-testid="volume-import-btn">
                          <span><Upload className="w-4 h-4 mr-1" /> Import CSV</span>
                        </Button>
                        <input type="file" accept=".csv" className="hidden" onChange={importVolumeCSV} />
                      </label>
                      <Button variant="outline" size="sm" onClick={resetVolumeMetrics} data-testid="volume-reset-btn">
                        <RotateCcw className="w-4 h-4 mr-1" /> Reset
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 flex gap-3 items-center">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by parent or sub-component..."
                        value={volumeFilter}
                        onChange={(e) => setVolumeFilter(e.target.value)}
                        className="pl-9"
                        data-testid="volume-search"
                      />
                    </div>
                    {Object.keys(volumeEditing).length > 0 && (
                      <Button onClick={saveVolumeMetrics} disabled={saving} className="bg-[#0F2040]" data-testid="volume-save-btn">
                        <Save className="w-4 h-4 mr-1" /> Save Changes
                      </Button>
                    )}
                  </div>

                  {volumeLoading ? (
                    <div className="text-center py-8 text-slate-400">Loading...</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="volume-metrics-table">
                        <thead>
                          <tr className="border-b-2 border-slate-200 bg-slate-50">
                            <th className="text-left py-2 px-3 font-semibold text-slate-600" style={{width:'25%'}}>Parent Component</th>
                            <th className="text-left py-2 px-3 font-semibold text-slate-600" style={{width:'30%'}}>Sub-Component</th>
                            <th className="text-right py-2 px-3 font-semibold text-slate-600" style={{width:'15%'}}>Simple (hrs/vol)</th>
                            <th className="text-right py-2 px-3 font-semibold text-slate-600" style={{width:'15%'}}>Standard (hrs/vol)</th>
                            <th className="text-right py-2 px-3 font-semibold text-slate-600" style={{width:'15%'}}>Complex (hrs/vol)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {volumeMetrics
                            .map((m, idx) => ({ ...m, _idx: idx }))
                            .filter(m =>
                              !volumeFilter ||
                              m.parent_component.toLowerCase().includes(volumeFilter.toLowerCase()) ||
                              m.sub_component.toLowerCase().includes(volumeFilter.toLowerCase())
                            )
                            .map((m) => (
                              <tr key={m._idx} className={`border-b border-slate-100 ${volumeEditing[m._idx] ? 'bg-amber-50/50' : 'hover:bg-slate-50/60'}`}>
                                <td className="py-1.5 px-3 text-slate-700 text-xs">{m.parent_component}</td>
                                <td className="py-1.5 px-3 text-slate-800 text-xs font-medium">{m.sub_component}</td>
                                <td className="py-1.5 px-3">
                                  <Input
                                    type="number" step="0.001" min="0"
                                    value={m.simple_hrs_per_vol}
                                    onChange={(e) => updateVolumeMetric(m._idx, 'simple_hrs_per_vol', e.target.value)}
                                    className="h-7 text-xs text-right w-24 ml-auto"
                                  />
                                </td>
                                <td className="py-1.5 px-3">
                                  <Input
                                    type="number" step="0.001" min="0"
                                    value={m.standard_hrs_per_vol}
                                    onChange={(e) => updateVolumeMetric(m._idx, 'standard_hrs_per_vol', e.target.value)}
                                    className="h-7 text-xs text-right w-24 ml-auto"
                                  />
                                </td>
                                <td className="py-1.5 px-3">
                                  <Input
                                    type="number" step="0.001" min="0"
                                    value={m.complex_hrs_per_vol}
                                    onChange={(e) => updateVolumeMetric(m._idx, 'complex_hrs_per_vol', e.target.value)}
                                    className="h-7 text-xs text-right w-24 ml-auto"
                                  />
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                      <p className="text-xs text-slate-400 mt-3">{volumeMetrics.length} metrics across {[...new Set(volumeMetrics.map(m => m.parent_component))].length} parent components</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Client Resources Tab */}
          {isSuperAdmin && (
            <TabsContent value="client-resources">
              <ClientResourceSettings />
            </TabsContent>
          )}

          {/* Named Resources Tab — Phase 2 of Workspace Overview */}
          {isSuperAdmin && (
            <TabsContent value="named-resources">
              <NamedResourcesTab />
            </TabsContent>
          )}

          <TabsContent value="estimates-history">
            <EstimatesHistorySettings navigate={navigate} />
          </TabsContent>

          {isSuperAdmin && (
            <TabsContent value="public-cloud-releases">
              <PublicCloudReleasesEditor />
            </TabsContent>
          )}

          {/* New Development Config Tab */}
          <TabsContent value="new-dev-config">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-['Outfit']">New Development T-Shirt Sizes</CardTitle>
                <p className="text-sm text-slate-500">Configure base hours, DE FTE allocation, and overhead uplift percentages for each development size.</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(newDevSizes).map(([size, config]) => {
                  const sizeLabel = size.charAt(0).toUpperCase() + size.slice(1);
                  return (
                    <div key={size} className="p-4 border border-violet-200 bg-violet-50/20 rounded-lg space-y-4" data-testid={`new-dev-size-config-${size}`}>
                      <h3 className="font-semibold text-[#0F2040]">{sizeLabel}</h3>
                      <div className="grid grid-cols-6 gap-3">
                        {[
                          { key: 'base_hours', label: 'Base Hours' },
                          { key: 'de_fte', label: 'DE FTE' },
                          { key: 'pm_uplift', label: 'PM Uplift %' },
                          { key: 'testing_uplift', label: 'Testing Uplift %' },
                          { key: 'training_uplift', label: 'Training Uplift %' },
                          { key: 'documentation_uplift', label: 'Doc Uplift %' },
                        ].map(({ key, label }) => (
                          <div key={key} className="space-y-1">
                            <Label className="text-xs text-slate-500">{label}</Label>
                            <Input
                              type="number"
                              step={key === 'base_hours' ? '1' : '0.01'}
                              min="0"
                              value={key.includes('uplift') ? Math.round(config[key] * 100 * 10) / 10 : config[key]}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setNewDevSizes(prev => ({
                                  ...prev,
                                  [size]: {
                                    ...prev[size],
                                    [key]: key.includes('uplift') ? val / 100 : val
                                  }
                                }));
                              }}
                              disabled={!isSuperAdmin}
                              className="text-center text-sm"
                              data-testid={`new-dev-${size}-${key}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}


const WEEK_NUMS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function ClientResourceSettings() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/planner/client-resources`, { withCredentials: true })
      .then(res => setRoles(res.data.roles))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API_URL]);

  const updatePct = (roleIdx, week, value) => {
    const v = Math.max(0, Math.min(100, parseInt(value) || 0));
    setRoles(prev => {
      const updated = [...prev];
      updated[roleIdx] = {
        ...updated[roleIdx],
        weekly_pct: { ...updated[roleIdx].weekly_pct, [week]: v }
      };
      return updated;
    });
    setDirty(true);
  };

  const updateField = (roleIdx, field, value) => {
    setRoles(prev => {
      const updated = [...prev];
      updated[roleIdx] = { ...updated[roleIdx], [field]: value };
      return updated;
    });
    setDirty(true);
  };

  const addRole = () => {
    setRoles(prev => [...prev, {
      role: `custom_${Date.now()}`,
      label: 'New Client Role',
      description: '',
      weekly_pct: Object.fromEntries(WEEK_NUMS.map(w => [w, 0])),
    }]);
    setDirty(true);
  };

  const removeRole = (idx) => {
    setRoles(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/planner/client-resources`, { roles }, { withCredentials: true });
      setDirty(false);
    } catch (e) {
      alert('Failed to save: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/planner/client-resources`, { withCredentials: true });
      setRoles(res.data.roles);
      setDirty(false);
    } catch (err) { logger.error('Failed to load client resources:', err); } finally { setLoading(false); }
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading client resource settings...</div>;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-['Outfit']">Client Resource Allocation</CardTitle>
            <CardDescription>Configure the expected client team involvement (% time commitment) per project week</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={reset} disabled={!dirty || saving} data-testid="client-resources-reset">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
            <Button size="sm" onClick={save} disabled={!dirty || saving}
              className="bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="client-resources-save">
              {saving ? 'Saving...' : <><Save className="w-3.5 h-3.5 mr-1" /> Save Changes</>}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] border-separate" style={{ borderSpacing: '1px' }}>
            <thead>
              <tr>
                <th className="text-left px-2 py-2 text-slate-500 font-medium w-48">Role</th>
                <th className="text-left px-2 py-2 text-slate-500 font-medium w-48">Description</th>
                {WEEK_NUMS.map(w => (
                  <th key={w} className="text-center px-1 py-2 text-slate-500 font-medium min-w-[44px]">W{w}</th>
                ))}
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {roles.map((cr, i) => (
                <tr key={cr.role + i} className="group">
                  <td className="px-2 py-1">
                    <Input value={cr.label} onChange={e => updateField(i, 'label', e.target.value)}
                      className="h-7 text-xs" data-testid={`cr-label-${i}`} />
                  </td>
                  <td className="px-2 py-1">
                    <Input value={cr.description || ''} onChange={e => updateField(i, 'description', e.target.value)}
                      className="h-7 text-xs" data-testid={`cr-desc-${i}`} />
                  </td>
                  {WEEK_NUMS.map(w => {
                    const pct = cr.weekly_pct?.[w] || 0;
                    return (
                      <td key={w} className="px-0.5 py-1">
                        <Input type="number" min={0} max={100}
                          value={pct} onChange={e => updatePct(i, w, e.target.value)}
                          className="h-7 text-xs text-center px-1 w-[44px]"
                          data-testid={`cr-pct-${i}-w${w}`}
                          style={{
                            backgroundColor: pct >= 50 ? '#0F2040' : pct >= 25 ? '#1E3A5F' : pct > 0 ? '#EFF6FF' : '',
                            color: pct >= 25 ? 'white' : '',
                          }} />
                      </td>
                    );
                  })}
                  <td className="px-1 py-1">
                    <button onClick={() => removeRole(i)} className="text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                      data-testid={`cr-delete-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Button variant="outline" size="sm" onClick={addRole} data-testid="cr-add-role">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Client Role
        </Button>

        <p className="text-[10px] text-slate-400">
          Values represent % time commitment per week (0-100). Changes apply to all new planner timelines. Existing saved exports are not affected.
        </p>
      </CardContent>
    </Card>
  );
}


function EstimatesHistorySettings({ navigate }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/estimates`, { withCredentials: true })
      .then(res => setEstimates(res.data?.estimates || []))
      .catch(() => toast.error('Failed to load estimates'))
      .finally(() => setLoading(false));
  }, [API_URL]);

  const deleteEstimate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this estimate?')) return;
    try {
      await axios.delete(`${API_URL}/api/estimates/${id}`, { withCredentials: true });
      setEstimates(prev => prev.filter(e => e.id !== id));
      toast.success('Estimate deleted');
    } catch (err) {
      logger.error('Failed to delete estimate:', err);
      toast.error('Failed to delete estimate');
    }
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading estimates...</div>;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-['Outfit']">Estimates History</CardTitle>
            <CardDescription>View and manage all saved estimates across your organization</CardDescription>
          </div>
          <Badge variant="outline" className="text-[10px]">{estimates.length} estimates</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {estimates.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">No estimates found.</p>
        ) : (
          <div className="space-y-3">
            {estimates.map((est) => (
              <div key={est.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`settings-estimate-${est.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-[#0F2040] truncate">{est.estimate_name}</span>
                    <Badge variant="outline" className="text-[9px] shrink-0">{est.implementation_type?.replace(/_/g, ' ')}</Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(est.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span>{est.results?.total_effort?.toLocaleString() || 0} hrs</span>
                    <span className="text-[#4ABFCF] font-medium">${est.results?.total_cost?.toLocaleString() || 0}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 ml-3">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/estimator?load=${est.id}`)}
                    className="h-7 w-7 p-0" data-testid={`settings-view-${est.id}`}>
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigate(`/estimator/quote/${est.id}`)}
                    className="h-7 w-7 p-0 text-[#4ABFCF] hover:text-[#3AAAB9] hover:border-[#4ABFCF]/40" data-testid={`settings-bc-${est.id}`}>
                    <Briefcase className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => deleteEstimate(est.id)}
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:border-red-300" data-testid={`settings-delete-${est.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

